import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LogOut, Moon, Sun, User, Bell, BellOff, BellRing, Phone, Mail, Check, X, Download, Hourglass, Send, Megaphone, Lock, Eye, EyeOff, LayoutDashboard, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useBranding } from '../context/BrandingContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import PersonalQrCard from '../components/PersonalQrCard';
import QrScanSheet from '../components/QrScanSheet';
import PasskeyManager from '../components/PasskeyManager';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../components/ui/drawer';

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe } = usePushNotifications();
  const [loading, setLoading] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [upiDraft, setUpiDraft] = useState('');       // iter217
  const [editingUpi, setEditingUpi] = useState(false); // iter217
  const [savingUpi, setSavingUpi] = useState(false);   // iter217
  const [savingEmail, setSavingEmail] = useState(false);

  // Change Password drawer
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  // P1.1: My pending outgoing transfers/requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showPendingDrawer, setShowPendingDrawer] = useState(false);
  const [nudgingId, setNudgingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  // iter120: scan-to-settle launch
  const [qrScanOpen, setQrScanOpen] = useState(false);

  // iter146 — Simple dashboard preferences.
  // iter163 — Default OFF (Advanced) again; first-time users get a picker
  // modal on Home before this Profile screen ever loads. Existing setting
  // honored regardless.
  const defaultSimple = user?.simple_dashboard === true;
  const [simpleDashboard, setSimpleDashboard] = useState(defaultSimple);
  const [settleThreshold, setSettleThreshold] = useState(Number(user?.settle_threshold ?? 1000));
  const [savingSimple, setSavingSimple] = useState(false);
  const [thresholdDraft, setThresholdDraft] = useState(String(Number(user?.settle_threshold ?? 1000)));

  const handleSimpleToggle = async (val) => {
    setSimpleDashboard(val);
    setSavingSimple(true);
    try {
      await api.put('/users/me', { simple_dashboard: val });
      toast.success(val ? 'Simple dashboard ON' : 'Detailed dashboard ON');
      if (refreshUser) await refreshUser();
    } catch (e) {
      setSimpleDashboard(!val);
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSimple(false);
    }
  };

  const handleThresholdSave = async () => {
    const v = Math.max(0, Math.min(100000, Number(thresholdDraft) || 0));
    if (v === settleThreshold) return;
    setSettleThreshold(v);
    try {
      await api.put('/users/me', { settle_threshold: v });
      toast.success(`Settle threshold: ₹${v}`);
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save threshold');
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const res = await api.get('/coin-transfers/my-outgoing');
      setPendingRequests(res.data || []);
    } catch {
      // Silent — chip just won't show
    }
  };

  useEffect(() => {
    fetchPendingRequests();
    const t = setInterval(fetchPendingRequests, 30000);
    return () => clearInterval(t);
  }, []);

  const handleNudge = async (id) => {
    setNudgingId(id);
    try {
      const res = await api.post(`/coin-transfers/${id}/nudge`);
      toast.success(res.data.message);
      fetchPendingRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to nudge');
    } finally {
      setNudgingId(null);
    }
  };

  const handleCancelTransfer = async (id) => {
    if (!window.confirm('Cancel this pending transfer?')) return;
    setCancellingId(id);
    try {
      await api.post(`/coin-transfers/${id}/cancel`);
      toast.success('Transfer cancelled');
      fetchPendingRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancellingId(null);
    }
  };

  useEffect(() => {
    api.get('/notification-preferences').then(res => setPrefs(res.data)).catch(() => {});
  }, []);

  const startEditEmail = () => {
    setEmailDraft(user?.email || '');
    setEditingEmail(true);
  };

  const saveEmail = async () => {
    const email = emailDraft.trim();
    if (email && !email.includes('@')) {
      toast.error('Invalid email');
      return;
    }
    setSavingEmail(true);
    try {
      await api.put('/users/me', { email });
      toast.success(email ? 'Email saved' : 'Email cleared');
      setEditingEmail(false);
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save email');
    } finally {      setSavingEmail(false);
    }
  };

  const handleLogout = () => { logout(); };

  // iter217 — UPI ID edit handlers
  const startEditUpi = () => {
    setUpiDraft(user?.upi_id || '');
    setEditingUpi(true);
  };

  const saveUpi = async () => {
    const upi = upiDraft.trim();
    if (upi && (!upi.includes('@') || upi.includes(' ') || upi.length < 4)) {
      toast.error("Invalid UPI ID (should look like 'name@bank')");
      return;
    }
    setSavingUpi(true);
    try {
      await api.put('/users/me', { upi_id: upi });
      toast.success(upi ? 'UPI ID saved — peers can now pay you in one tap' : 'UPI ID cleared');
      setEditingUpi(false);
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save UPI ID');
    } finally {
      setSavingUpi(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPwd) { toast.error('Enter your current password'); return; }
    if (newPwd.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPwd !== confirmPwd) { toast.error('New passwords do not match'); return; }
    if (newPwd === currentPwd) { toast.error('New password must be different from current'); return; }
    setChangingPwd(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPwd, new_password: newPwd });
      toast.success('Password changed successfully');
      setShowChangePwd(false);
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      setShowCurrent(false); setShowNew(false); setShowConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPwd(false);
    }
  };

  const handlePushToggle = async () => {
    setLoading(true);
    try {
      if (isSubscribed) { await unsubscribe(); toast.success('Push notifications disabled'); }
      else { await subscribe(); toast.success('Push notifications enabled!'); }
    } catch (error) {
      toast.error(error.message === 'Permission denied' ? 'Please allow notifications in browser settings' : 'Failed to update push notifications');
    } finally { setLoading(false); }
  };

  const handlePrefChange = async (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    setSavingPrefs(true);
    try {
      await api.put('/notification-preferences', updated);
    } catch { toast.error('Failed to save preference'); }
    finally { setSavingPrefs(false); }
  };

  const prefItems = [
    { key: 'mute_all', label: 'Mute All Notifications', desc: 'Silence everything (in-app + WhatsApp)' },
    { key: 'mute_expenses', label: 'Mute Expenses', desc: 'No alerts for expense submissions/approvals' },
    { key: 'mute_fines', label: 'Mute Fines', desc: 'No alerts for fines, reversals, or money allotments' },
    { key: 'mute_sessions', label: 'Mute Sessions', desc: 'No alerts for attendance sessions' },
    { key: 'mute_admin_actions', label: 'Mute Admin Actions', desc: 'No alerts for member joins, admin promotions, balance warnings' },
    { key: 'mute_reminders', label: 'Mute Reminders', desc: 'No balance reminder messages' },
  ];

  return (
    <div data-testid="profile-page">
      <h1 className="font-display text-2xl font-bold mb-6">Profile</h1>

      {/* User Info Card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Card className="p-6 border-2 rounded-2xl mb-6" data-testid="user-info-card">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="font-display text-2xl font-bold text-primary">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl font-bold">{user?.name}</h2>
              <p className="text-muted-foreground flex items-center gap-1 text-sm"><Phone className="w-3.5 h-3.5" /> {user?.phone}</p>
              {!editingEmail ? (
                <button
                  onClick={startEditEmail}
                  className="text-muted-foreground flex items-center gap-1 text-sm hover:text-primary transition-colors mt-1"
                  data-testid="edit-email-btn"
                >
                  <Mail className="w-3.5 h-3.5" />
                  {user?.email || <span className="italic">Add email for notifications</span>}
                </button>
              ) : (
                <div className="flex items-center gap-1 mt-1" data-testid="email-edit-row">
                  <Input
                    type="email"
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    placeholder="you@example.com"
                    className="h-8 text-sm flex-1 max-w-[220px]"
                    autoFocus
                    data-testid="email-input"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={saveEmail}
                    disabled={savingEmail}
                    className="h-8 w-8"
                    data-testid="email-save-btn"
                  >
                    <Check className="w-4 h-4 text-green-500" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setEditingEmail(false)}
                    className="h-8 w-8"
                    data-testid="email-cancel-btn"
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              )}
              {/* iter217 — UPI ID row for one-tap peer payments */}
              {!editingUpi ? (
                <button
                  onClick={startEditUpi}
                  className="text-muted-foreground flex items-center gap-1 text-sm hover:text-primary transition-colors mt-1"
                  data-testid="edit-upi-btn"
                >
                  <span className="text-[13px] leading-none">💳</span>
                  {user?.upi_id ? (
                    <span className="tabular-nums text-xs">{user.upi_id}</span>
                  ) : (
                    <span className="italic text-xs">Add UPI ID for one-tap peer payments</span>
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-1 mt-1" data-testid="upi-edit-row">
                  <Input
                    value={upiDraft}
                    onChange={(e) => setUpiDraft(e.target.value)}
                    placeholder="e.g. 9820030046@paytm"
                    className="h-8 text-sm flex-1 max-w-[220px]"
                    autoFocus
                    data-testid="upi-input"
                  />
                  <Button size="icon" variant="ghost" onClick={saveUpi} disabled={savingUpi} className="h-8 w-8" data-testid="upi-save-btn">
                    <Check className="w-4 h-4 text-green-500" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditingUpi(false)} className="h-8 w-8" data-testid="upi-cancel-btn">
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* iter120: Personal QR + Scan launcher */}
      <PersonalQrCard user={user} />
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.08 }}>
        <Card className="p-4 border-2 rounded-2xl mb-6" data-testid="scan-launcher-card">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-display font-bold">Pay someone via QR</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Scan a friend's SplitEasy QR to settle a debt across any of your shared groups.
              </p>
            </div>
            <Button
              size="sm"
              className="rounded-full text-xs shrink-0"
              onClick={() => setQrScanOpen(true)}
              data-testid="profile-scan-qr-btn"
            >
              Scan QR
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* P1.1: My pending requests chip */}
      {pendingRequests.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05 }} className="mb-6">
          <button
            onClick={() => setShowPendingDrawer(true)}
            data-testid="pending-requests-chip"
            className="w-full p-4 rounded-2xl border-2 border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 transition-colors flex items-center justify-between"
          >
            <div className="flex items-center gap-3 text-left">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Hourglass className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-display font-bold text-sm">{pendingRequests.length} pending request{pendingRequests.length === 1 ? '' : 's'}</p>
                <p className="text-xs text-muted-foreground">Tap to view & nudge</p>
              </div>
            </div>
            <span className="font-display font-bold text-2xl text-amber-600" data-testid="pending-requests-count">
              {pendingRequests.length}
            </span>
          </button>
        </motion.div>
      )}

      <Drawer open={showPendingDrawer} onOpenChange={setShowPendingDrawer}>
        <DrawerContent data-testid="pending-requests-drawer">
          <DrawerHeader>
            <DrawerTitle className="font-display text-xl flex items-center gap-2">
              <Hourglass className="w-5 h-5 text-primary" />
              Awaiting action
            </DrawerTitle>
            <p className="text-sm text-muted-foreground">Things you've sent that are waiting on someone else.</p>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[65vh] overflow-y-auto space-y-2">
            {pendingRequests.length === 0 && (
              <p className="text-center text-muted-foreground py-8">All caught up!</p>
            )}
            {pendingRequests.map((t) => {
              const isRequest = t.direction === 'request';
              const isFundReq = isRequest && t.from_user_id === 'group_fund';
              const counterParty = isRequest ? (isFundReq ? `${t.group_name} admins` : t.from_user_name) : t.to_user_name;
              const verb = isRequest ? 'Requested from' : 'Sent to';
              return (
                <Card key={t.id} className="p-3 border rounded-xl space-y-2" data-testid={`pending-item-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {isRequest ? <BellRing className="w-4 h-4 text-amber-500 shrink-0" /> : <Send className="w-4 h-4 text-blue-500 shrink-0" />}
                        <p className="font-semibold text-sm truncate">
                          {Math.round(t.amount)} · {t.group_name}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{verb} {counterParty}</p>
                      {t.note && <p className="text-xs italic mt-1">"{t.note}"</p>}
                      {t.last_nudged_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Last nudged: {new Date(t.last_nudged_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleNudge(t.id)}
                      disabled={nudgingId === t.id}
                      data-testid={`nudge-${t.id}`}
                      className="rounded-full text-xs flex-1"
                    >
                      <Megaphone className="w-3.5 h-3.5 mr-1" />
                      {nudgingId === t.id ? 'Nudging…' : 'Nudge'}
                    </Button>
                    {!isRequest && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCancelTransfer(t.id)}
                        disabled={cancellingId === t.id}
                        data-testid={`cancel-${t.id}`}
                        className="rounded-full text-xs text-red-500 hover:text-red-600"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
          <DrawerClose asChild>
            <Button variant="outline" className="mx-4 mb-4 rounded-full" data-testid="close-pending-btn">Close</Button>
          </DrawerClose>
        </DrawerContent>
      </Drawer>

      {/* Settings */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
        <h2 className="font-display text-lg font-bold mb-4">Settings</h2>
        <Card className="border-2 rounded-2xl overflow-hidden">
          <div className="p-4 flex items-center justify-between border-b border-border">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}
              <div>
                <Label className="font-semibold">Dark Mode</Label>
                <p className="text-sm text-muted-foreground">{theme === 'dark' ? 'Currently enabled' : 'Currently disabled'}</p>
              </div>
            </div>
            <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="theme-toggle" />
          </div>
          {isSupported && (
            <div className="p-4 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-3">
                {isSubscribed ? <Bell className="w-5 h-5 text-primary" /> : <BellOff className="w-5 h-5 text-muted-foreground" />}
                <div>
                  <Label className="font-semibold">Push Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    {isSubscribed ? 'Receive instant alerts' : permission === 'denied' ? 'Blocked in browser' : 'Enable browser alerts'}
                  </p>
                </div>
              </div>
              <Switch checked={isSubscribed} onCheckedChange={handlePushToggle} disabled={loading || permission === 'denied'} data-testid="push-toggle" />
            </div>
          )}
          {/* iter195 — Passkey / Face ID enrollment + management. */}
          <PasskeyManager userPhone={user?.phone} />
          {/* iter146 — Simple dashboard toggle.
              iter186 — Hidden but kept in DOM-tree-commented-out form so the
              one-week revert is a quick uncomment. Will be deleted in cleanup. */}
          {/*
          <div className="p-4 flex items-center justify-between border-b border-border">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-5 h-5 text-primary" />
              <div>
                <Label className="font-semibold">Show simple dashboard</Label>
                <p className="text-sm text-muted-foreground">
                  Clean member-first home — 4 big cards instead of the dense view
                </p>
              </div>
            </div>
            <Switch
              checked={simpleDashboard}
              onCheckedChange={handleSimpleToggle}
              disabled={savingSimple}
              data-testid="toggle-simple-dashboard"
            />
          </div>
          */}
          {/* iter146 — Settle Now threshold (only meaningful when simple dashboard is ON)
              iter186 — Hidden along with the Simple toggle. */}
          {false && simpleDashboard && (
            <div className="p-4 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-3">
                <Flame className="w-5 h-5 text-orange-500" />
                <div>
                  <Label className="font-semibold">Settle-Now threshold</Label>
                  <p className="text-sm text-muted-foreground">
                    Pin a debt to the hero when it's ≥ this amount
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">₹</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={thresholdDraft}
                  onChange={(e) => setThresholdDraft(e.target.value)}
                  onBlur={handleThresholdSave}
                  className="w-20 h-8 text-sm text-right"
                  data-testid="input-settle-threshold"
                />
              </div>
            </div>
          )}
          <div className="p-4 flex items-center gap-3">
            <User className="w-5 h-5 text-muted-foreground" />
            <div>
              <Label className="font-semibold">Account</Label>
              <p className="text-sm text-muted-foreground">Member since {new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Notification Preferences (#4) */}
      {prefs && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.15 }} className="mt-6">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-primary" />
            Notification Preferences
          </h2>
          <Card className="border-2 rounded-2xl overflow-hidden" data-testid="notification-prefs-card">
            {prefItems.map(({ key, label, desc }, i) => (
              <div key={key} className={`p-4 flex items-center justify-between ${i < prefItems.length - 1 ? 'border-b border-border' : ''}`}>
                <div>
                  <Label className="font-medium">{label}</Label>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
                <Switch
                  checked={prefs[key] || false}
                  onCheckedChange={(val) => handlePrefChange(key, val)}
                  disabled={savingPrefs || (key !== 'mute_all' && prefs.mute_all)}
                  data-testid={`pref-${key}`}
                />
              </div>
            ))}
          </Card>
        </motion.div>
      )}

      {/* Logout Button */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }} className="mt-8 space-y-3">
        <Button
          variant="outline"
          onClick={() => setShowChangePwd(true)}
          data-testid="open-change-password-btn"
          className="w-full h-12 rounded-full border-2 font-bold uppercase tracking-wider"
        >
          <Lock className="w-5 h-5 mr-2" />
          Change Password
        </Button>
        <Button
          variant="outline"
          onClick={() => window.dispatchEvent(new Event('open-install-prompt'))}
          data-testid="install-app-btn"
          className="w-full h-12 rounded-full border-2 font-bold uppercase tracking-wider"
        >
          <Download className="w-5 h-5 mr-2" />
          Install on home screen
        </Button>
        <Button
          variant="outline"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full h-14 rounded-full border-2 border-destructive text-destructive hover:bg-destructive hover:text-white font-bold uppercase tracking-wider"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Logout
        </Button>
      </motion.div>

      {/* Change Password Drawer */}
      <Drawer open={showChangePwd} onOpenChange={(o) => { setShowChangePwd(o); if (!o) { setCurrentPwd(''); setNewPwd(''); setConfirmPwd(''); } }}>
        <DrawerContent className="px-4 pb-6" data-testid="change-password-drawer">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Change Password</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-1">
            <div className="space-y-1.5">
              <Label htmlFor="cp-current" className="text-xs uppercase font-bold tracking-wider">Current password</Label>
              <div className="relative">
                <Input
                  id="cp-current"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)}
                  placeholder="Enter your current password"
                  className="h-12 pr-10 rounded-xl"
                  autoComplete="current-password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-current-password"
                  aria-label={showCurrent ? 'Hide password' : 'Show password'}
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-new" className="text-xs uppercase font-bold tracking-wider">New password</Label>
              <div className="relative">
                <Input
                  id="cp-new"
                  type={showNew ? 'text' : 'password'}
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-12 pr-10 rounded-xl"
                  autoComplete="new-password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-new-password"
                  aria-label={showNew ? 'Hide password' : 'Show password'}
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-confirm" className="text-xs uppercase font-bold tracking-wider">Confirm new password</Label>
              <div className="relative">
                <Input
                  id="cp-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  placeholder="Re-enter new password"
                  className="h-12 pr-10 rounded-xl"
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-confirm-password"
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <DrawerClose asChild>
                <Button variant="outline" className="flex-1 h-12 rounded-full font-bold uppercase tracking-wider" data-testid="cancel-change-password-btn">
                  Cancel
                </Button>
              </DrawerClose>
              <Button
                onClick={handleChangePassword}
                disabled={changingPwd || !currentPwd || !newPwd || !confirmPwd}
                className="flex-1 h-12 rounded-full font-bold uppercase tracking-wider"
                data-testid="submit-change-password-btn"
              >
                {changingPwd ? 'Saving…' : 'Update'}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* App Info */}
      <BrandFooter />

      {/* iter120: QR scanner sheet — launched from profile-scan-qr-btn */}
      <QrScanSheet open={qrScanOpen} onOpenChange={setQrScanOpen} />
    </div>
  );
}

function BrandFooter() {
  const { branding } = useBranding();
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.3 }} className="mt-8 text-center text-muted-foreground text-sm">
      <p className="font-display font-bold text-foreground">{branding.app_name || 'SplitEasy'}</p>
      {branding.footer_text ? (
        <p className="mt-1">{branding.footer_text}</p>
      ) : (
        <p className="mt-1">Version 1.0.0</p>
      )}
    </motion.div>
  );
}
